import { get, set } from 'lodash';

import { matches, SelectorOn } from './selector';
import { ConditionalRule, FieldDef, FieldRule, FieldValidator, ItemFieldSpec, Variant } from './types';

export interface FieldPolicyConfig<TCtx extends object, TFields extends Record<string, FieldDef<SelectorOn<any>, any>>, TValues = unknown> {
  fields: TFields;
  /**
   * The variant cells this context selects, applied in order - later ones win.
   *
   * A list rather than a single cell because variant axes are not always one product: a feature may
   * key its main grid on (country, flow) and still layer a smaller, orthogonal overlay on top
   * without multiplying out every combination into its own file.
   *
   * Takes a lookup rather than a flat `Record<key, Variant>` so a feature keeps its own exhaustive
   * `satisfies Record<...>` on whatever shape its variant map has - that assertion is the
   * add-a-country-breaks-the-build check, and it should stay where a reviewer can see it.
   */
  variantsFor: (ctx: TCtx) => (Variant<SelectorOn<any>, string, TValues> | undefined)[];
  /** Runs last, after base rules and the overlay. */
  lock?: (rule: FieldRule, ctx: TCtx) => FieldRule;
  /**
   * The codelist sources for this context, applied to every field's rule.
   *
   * A default rather than a fixed value: it is seeded before `base`, so a field or a variant can
   * override it. Lives in the config rather than being injected at render so `resolve` gives the
   * same answer to the UI and to the node tests.
   */
  systemsFor?: (ctx: TCtx) => string[];
  /**
   * Where the initial disabled state comes from.
   *
   * Defaults to `ctx.readOnly` when the context has one - read-only is a generic UI notion, so the
   * common case needs no wiring. `lock` stays explicit on purpose: it encodes a domain rule.
   */
  readOnly?: (ctx: TCtx) => boolean;
}

/** A schema sees its own value; a function also sees the whole form, for cross-field rules. */
const runValidator = (validator: FieldValidator<any> | undefined, value: unknown, values: unknown): string | undefined => {
  if (!validator) return undefined;

  if (typeof validator === 'function') return validator(values, value);

  const result = validator.safeParse(value);

  return result.success ? undefined : result.error?.issues?.[0]?.message;
};

const applyRules = <TCtx extends object>(rule: FieldRule, rules: ConditionalRule<SelectorOn<any>>[], ctx: TCtx): FieldRule =>
  rules.reduce((acc, r) => (matches(r.when, ctx) ? { ...acc, ...r.then } : acc), rule);

/**
 * Per-item errors for one array field, keyed `<path>.<index>.<subPath>` - exactly how Mantine
 * already keys form errors for an indexed path, so nothing else needs to know these come from a
 * different loop than `buildErrors`'s own per-path one.
 *
 * An item's own visibility never varies (see `ItemFieldSpec`), so every item is walked unconditionally
 * once the array itself is present - there is no per-item `resolve` to consult. Recurses once per
 * nested `items` (e.g. a container's `seals`), the same one-level-at-a-time shape `ItemFieldSpec`
 * itself uses.
 */
const collectItemErrors = (
  path: string,
  items: Record<string, ItemFieldSpec<any>>,
  arrayValue: unknown,
  values: unknown,
  acc: Record<string, string>,
): Record<string, string> => {
  if (!Array.isArray(arrayValue)) return acc;

  return arrayValue.reduce(
    (outer, item, index) =>
      Object.entries(items).reduce((inner, [subPath, spec]) => {
        const itemPath = `${path}.${index}.${subPath}`;
        const subValue = get(item, subPath);

        if (spec.items) {
          const nestedFields = typeof spec.items === 'function' ? spec.items(index) : spec.items;
          return collectItemErrors(itemPath, nestedFields, subValue, values, inner);
        }

        const message = runValidator(spec.validate, subValue, values);

        return message ? { ...inner, [itemPath]: message } : inner;
      }, outer),
    acc,
  );
};

/**
 * Builds a field policy over a feature's own context type and variant map.
 *
 * Resolution precedence, stated once: base -> base rules -> variant overlay -> lock.
 */
/** Splits the registry into the path sets the policy exposes. */
const classifyPaths = <TFields extends Record<string, FieldDef<SelectorOn<any>, any>>>(fields: TFields) => {
  const allPaths = Object.keys(fields) as (keyof TFields & string)[];
  const defOf = (path: keyof TFields & string) => fields[path] as FieldDef<SelectorOn<any>, any>;

  return {
    allPaths,
    defOf,
    policyPaths: allPaths.filter((path) => (defOf(path).visibility ?? 'policy') === 'policy'),
    derivedPaths: allPaths.filter((path) => ['path', 'read'].includes(defOf(path).source ?? 'path')),
    viewModelPaths: allPaths.filter((path) => defOf(path).viewModel === true),
    // `base` is required as well as `validate`: resolve() refuses a jsx-owned path, so a validator
    // on one could never be evaluated. An invariant test keeps that from being written by accident.
    validatedPaths: allPaths.filter((path) => Boolean(defOf(path).validate) && Boolean(defOf(path).base)),
  };
};

/**
 * One policy per registry, keyed on the `fields` object.
 *
 * Load-bearing, not an optimisation: a config can be handed to `useFormPolicy` at a call site AND
 * composed in a pure module for the form initialiser and the node tests. Both paths must land on
 * the SAME object, because `usePolicyScope` asserts the scope was opened with the policy its
 * consumers hold - a second instance makes every consumer throw.
 */
const built = new WeakMap<object, unknown>();

export const createFieldPolicy = <TCtx extends object, TFields extends Record<string, FieldDef<SelectorOn<any>, any>>, TValues = unknown>(
  config: FieldPolicyConfig<TCtx, TFields, TValues>,
) => {
  const cached = built.get(config.fields) as ReturnType<typeof buildFieldPolicy<TCtx, TFields, TValues>> | undefined;

  if (cached) return cached;

  const policy = buildFieldPolicy(config);

  built.set(config.fields, policy);

  return policy;
};

interface SeederParts<TCtx extends object, TFields extends Record<string, FieldDef<SelectorOn<any>, any>>> {
  defOf: (path: keyof TFields & string) => FieldDef<SelectorOn<any>, any>;
  derivedPaths: (keyof TFields & string)[];
  policyPaths: (keyof TFields & string)[];
  resolve: (path: keyof TFields & string, ctx: TCtx) => FieldRule;
}

/**
 * Turns an entity into the values a form starts with.
 *
 * Its own factory so the policy factory stays about resolution. Two decisions live here:
 *
 * - a policy-owned field the context makes ABSENT is not seeded, so what the user cannot see is
 *   neither validated nor submitted. Safe because `present` is selected on axes the entity carries
 *   or on fixed app settings, so it cannot change without a re-seed - see `useFormPolicy`. Paths
 *   the policy does not own are always seeded; `resolve` refuses them anyway.
 * - `set` per path rather than lodash `merge`: merge skips `undefined` sources, so a field whose
 *   entity value is undefined would end up absent instead of present-and-undefined, which changes
 *   what gets submitted.
 *
 * With no `ctx` nothing is filtered - the shape the golden master uses to inspect the registry
 * itself, independent of any one variant.
 */
const createSeeder = <TCtx extends object, TFields extends Record<string, FieldDef<SelectorOn<any>, any>>>({
  defOf,
  derivedPaths,
  policyPaths,
  resolve,
}: SeederParts<TCtx, TFields>) => {
  type Path = keyof TFields & string;

  const readValue = (path: Path, entity: unknown): unknown => {
    const def = defOf(path);
    return def.source === 'read' ? def.read?.(entity) : get(entity, path);
  };

  const seeds = (path: Path, ctx?: TCtx) => !ctx || !policyPaths.includes(path) || resolve(path, ctx).present;

  return {
    applyDerivedValues: <T extends object>(explicit: T, entity: unknown, ctx?: TCtx): T =>
      derivedPaths.reduce((acc, path) => (seeds(path, ctx) ? set(acc, path, readValue(path, entity)) : acc), explicit),
  };
};

const buildFieldPolicy = <TCtx extends object, TFields extends Record<string, FieldDef<SelectorOn<any>, any>>, TValues = unknown>(
  config: FieldPolicyConfig<TCtx, TFields, TValues>,
) => {
  type Path = keyof TFields & string;

  const { variantsFor, lock, systemsFor } = config;
  const readOnly = config.readOnly ?? ((ctx: TCtx) => Boolean((ctx as { readOnly?: unknown }).readOnly));
  const { allPaths, defOf, policyPaths, derivedPaths, viewModelPaths, validatedPaths } = classifyPaths(config.fields);

  const applyOverlays = (rule: FieldRule, path: Path, ctx: TCtx): FieldRule =>
    variantsFor(ctx).reduce((acc, variant) => {
      const overlay = variant?.[path];

      if (!overlay) return acc;

      // `validate` has no slot on FieldRule - stripped here so it never leaks into the resolved
      // rule; `validatorFor` reads it straight off the variant instead.
      const { rules = [], validate: _validate, ...flat } = overlay;

      return applyRules({ ...acc, ...flat }, rules, ctx);
    }, rule);

  /**
   * The effective validator for one field in one context: the LAST variant (in `variantsFor`
   * order) that declares one, falling back to the base `FieldDef`'s own. Mirrors `applyOverlays`'s
   * later-wins precedence, kept separate because `validate` cannot live on a `FieldRule`.
   */
  const validatorFor = (path: Path, ctx: TCtx): FieldValidator<TValues> | undefined =>
    variantsFor(ctx).reduce<FieldValidator<TValues> | undefined>((acc, variant) => variant?.[path]?.validate ?? acc, defOf(path).validate);

  const resolve = (path: Path, ctx: TCtx): FieldRule => {
    const def = defOf(path);

    if (!def.base) {
      // Registered for form state only: visibility still lives in the component's JSX and has not
      // been transcribed. Returning a neutral rule would silently claim authority it lacks.
      throw new Error(`resolve: "${path}" is registered for form state only (visibility: 'jsx')`);
    }

    // `items` is copied from `def.items` unchanged, never from `def.base` (excluded there) or any
    // rule/overlay (excluded from their types too) - it never varies by context, see `ItemFieldSpec`.
    const seed = {
      amendmentLocked: false,
      systems: systemsFor?.(ctx),
      ...def.base,
      disabled: Boolean(readOnly(ctx)),
      items: def.items,
      createNewItem: def.createNewItem,
    };
    const withBase = applyRules(seed, def.rules ?? [], ctx);
    const resolved = applyOverlays(withBase, path, ctx);

    return lock ? lock(resolved, ctx) : resolved;
  };

  const { applyDerivedValues } = createSeeder<TCtx, TFields>({ defOf, derivedPaths, policyPaths, resolve });

  /**
   * Per-field errors for one context, keyed by path.
   *
   * Dotted keys on purpose: that is exactly how Mantine keys form errors - its own zodResolver does
   * `error.path.join('.')` - and it is already this registry's key format. So nothing has to
   * assemble a nested schema mirroring the form's shape.
   *
   * Absent fields are skipped rather than reported. Only the FIRST issue per field is surfaced,
   * because a single input can show one message.
   */
  const buildErrors = (values: unknown, ctx: TCtx): Record<string, string> =>
    // Over `policyPaths`, not `validatedPaths`: the latter is fixed at build time from `base` alone
    // and would miss a path whose ONLY validator comes from a variant (see `validatorFor`).
    policyPaths.reduce(
      (acc, path) => {
        const resolved = resolve(path, ctx);

        if (!resolved.present) return acc;

        const message = runValidator(validatorFor(path, ctx), get(values, path), values);
        const withOwnError = message ? { ...acc, [path]: message } : acc;
        // The RESOLVED items, not `defOf(path).items` - a variant can narrow this (e.g. import
        // omitting a container's `seals`), and validation must never require a sub-field the current
        // variant doesn't even render.
        const items = resolved.items;

        return items ? collectItemErrors(path, items, get(values, path), values, withOwnError) : withOwnError;
      },
      {} as Record<string, string>,
    );

  return {
    allPaths,
    policyPaths,
    derivedPaths,
    viewModelPaths,
    validatedPaths,
    buildErrors,
    resolve,
    resolveAll: (ctx: TCtx) => Object.fromEntries(policyPaths.map((path) => [path, resolve(path, ctx)])) as Record<Path, FieldRule>,
    buildInitialValues: (entity: unknown, ctx?: TCtx) => applyDerivedValues({} as Record<string, unknown>, entity, ctx),
    applyDerivedValues,
  };
};

export type FieldPolicy<TCtx extends object, TFields extends Record<string, FieldDef<SelectorOn<any>, any>>, TValues = unknown> = ReturnType<
  typeof createFieldPolicy<TCtx, TFields, TValues>
>;
